/**
 * RentCast rent-transparency components extracted from AnalysisModal.jsx.
 *
 * - `trackEvent`  : fire-and-forget analytics helper
 * - `RentCastUnlockCard`   : paywall-gated unlock CTA (renders inside an analysis view)
 * - `RENT_SOURCE_LABELS` + `CONFIDENCE_STYLE` + `DataSourcesBadge` : transparency badge for
 *    rent / taxes / insurance sources
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Check, Lock, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { API, formatCurrency } from "../../lib/helpers";

// Fire-and-forget analytics helper — posts to /api/analytics/track. Silent on failure.
export const trackEvent = (eventType, metadata = {}) => {
  try {
    axios.post(`${API}/analytics/track`, { event_type: eventType, metadata })
      .catch(() => {});
  } catch (_) { /* ignore */ }
};

// Human-readable rent source labels used across the analysis UI.
export const RENT_SOURCE_LABELS = {
  listing_description: 'Tenant Rent (listing)',
  rentcast: 'RentCast estimate',
  local_comps: 'Local Comps',
  zip_comparable: 'Local Rental Comps',
  zillow_zestimate: 'Zillow Estimate',
  market_estimate: 'Market Comps',
  state_average: 'State Average',
  user_override: 'Your Input',
  cache: 'Cached',
  fallback: 'Fallback',
};

export const CONFIDENCE_STYLE = {
  high: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  medium: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  low: 'bg-red-500/10 text-red-500 border-red-500/20',
};

/**
 * Shows a compact badge indicating the source + confidence of a data value.
 * For rent rows, displays rich context (source + comp count + confidence).
 * For tax/insurance, shows a simple "Estimated" vs "API Data" pill.
 */
export const DataSourcesBadge = ({ source }) => {
  if (!source) return null;

  const confidence = source.confidence;
  const src = source.source;
  const compCount = source.comp_count || 0;

  if (confidence && src) {
    const style = CONFIDENCE_STYLE[confidence] || CONFIDENCE_STYLE.low;
    const label = RENT_SOURCE_LABELS[src] || src;
    const confLabel = confidence.charAt(0).toUpperCase() + confidence.slice(1);
    const title = compCount > 0
      ? `Source: ${label} · ${compCount} comps · Confidence: ${confLabel}`
      : `Source: ${label} · Confidence: ${confLabel}`;
    return (
      <span
        title={title}
        data-testid={`rent-source-badge-${src}`}
        className={`inline-flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${style}`}
      >
        {label}{compCount > 0 ? ` · ${compCount}` : ''} · {confLabel}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded-full ${
      source.is_estimated
        ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
        : 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
    }`}>
      {source.is_estimated ? 'Estimated' : 'API Data'}
    </span>
  );
};

/**
 * Inline RentCast "Unlock" card.
 * Renders ONLY when rent_source is NOT rentcast AND confidence is medium/low.
 * Click flow:
 *   - Anonymous → parent onUpgrade() (signup prompt)
 *   - Free user → parent onUpgrade() (upgrade prompt)
 *   - Pro user  → POST /api/properties/{id}/rentcast-unlock, swap to verified card
 */
export const RentCastUnlockCard = ({ property, analysis, isPro, isElite, isAuthed, onUpgrade }) => {
  const [loading, setLoading] = useState(false);
  const [unlocked, setUnlocked] = useState(null);
  const [error, setError] = useState(null);

  const rent = analysis?.data_sources?.rent;
  const shouldShow =
    rent &&
    rent.source !== 'rentcast' &&
    (rent.confidence === 'low' || rent.confidence === 'medium') &&
    !unlocked;

  // Fire `rentcast_unlock_viewed` once when CTA mounts for a property
  useEffect(() => {
    if (shouldShow && property?.id) {
      trackEvent('rentcast_unlock_viewed', {
        property_id: String(property.id),
        current_source: rent.source,
        current_confidence: rent.confidence,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?.id]);

  // Elite auto-unlock — Elite tier has 500 RentCast/mo, plenty for normal use,
  // so we silently fetch the verified rent on mount instead of showing a CTA
  // that *looks* like a paywall (confused users into thinking Elite is locked).
  useEffect(() => {
    if (!shouldShow) return;
    if (!isElite) return;
    if (loading || unlocked) return;
    let cancelled = false;
    const auto = async () => {
      try {
        setLoading(true);
        const resp = await axios.post(`${API}/properties/${property.id}/rentcast-unlock`);
        if (cancelled) return;
        const data = resp.data || {};
        if (data.status === 'ok') {
          setUnlocked(data);
          trackEvent('rentcast_verified_loaded', {
            property_id: String(property.id),
            rent: data.rent,
            comp_count: data.comparable_count,
            from_cache: data.from_cache,
            tier: 'elite_auto',
          });
        }
      } catch {
        // Silent — Elite users will just see the regular estimate, not a broken UI.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    auto();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isElite, shouldShow, property?.id]);

  if (!shouldShow && !unlocked) return null;

  // Elite users get the auto-unlock effect above. Don't render a "locked"
  // CTA while it's in flight — keeps the UI clean for the highest tier.
  if (isElite && !unlocked) {
    if (loading) {
      return (
        <Card className="bg-emerald-500/5 border-emerald-500/20" data-testid="rentcast-elite-loading">
          <CardContent className="px-3 py-2 flex items-center gap-2 text-[11px] text-muted-foreground">
            <RefreshCw className="w-3 h-3 animate-spin text-emerald-500" />
            Pulling RentCast-verified rent…
          </CardContent>
        </Card>
      );
    }
    // No loading, no unlocked, no shouldShow change → silent
    return null;
  }

  const handleUnlock = async () => {
    trackEvent('rentcast_unlock_clicked', {
      property_id: String(property.id),
      current_source: rent.source,
    });

    if (!isAuthed) {
      if (onUpgrade) onUpgrade('rentcast_unlock');
      return;
    }
    if (!isPro) {
      trackEvent('rentcast_upgrade_started', { property_id: String(property.id) });
      if (onUpgrade) onUpgrade('rentcast_unlock');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const resp = await axios.post(`${API}/properties/${property.id}/rentcast-unlock`);
      const data = resp.data || {};
      if (data.status === 'ok') {
        setUnlocked(data);
        trackEvent('rentcast_verified_loaded', {
          property_id: String(property.id),
          rent: data.rent,
          comp_count: data.comparable_count,
          from_cache: data.from_cache,
        });
      } else if (data.status === 'limit_reached') {
        setError(data.message || 'Monthly unlock limit reached.');
        trackEvent('rentcast_cap_reached_frontend', {
          property_id: String(property.id),
          used: data.usage?.used,
          cap: data.usage?.cap,
        });
      } else if (data.status === 'no_data') {
        setError('No verified comps available for this address.');
      } else {
        setError('Unable to fetch verified rent.');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 || detail?.upgrade_required) {
        if (onUpgrade) onUpgrade('rentcast_unlock');
      } else if (e?.response?.status === 401 || detail?.signup_required) {
        if (onUpgrade) onUpgrade('rentcast_unlock');
      } else {
        setError(typeof detail === 'string' ? detail : (detail?.message || 'Error'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Unlocked state — show verified rent inline (no popup)
  if (unlocked && unlocked.rent) {
    return (
      <div
        data-testid="rentcast-unlocked-card"
        className="my-3 p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Check className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-500">
                RentCast Verified · High Confidence
              </span>
            </div>
            <p className="text-sm font-mono font-bold text-emerald-400">
              {formatCurrency(unlocked.rent)}/mo
              {unlocked.rent_range_low && unlocked.rent_range_high && (
                <span className="ml-2 text-[10px] text-muted-foreground font-normal">
                  range {formatCurrency(unlocked.rent_range_low)}–{formatCurrency(unlocked.rent_range_high)}
                </span>
              )}
            </p>
            <p className="text-[10px] text-muted-foreground mt-1">
              Based on {unlocked.comparable_count || 0} comparable rentals.
              {unlocked.from_cache && ' (cached)'}
              {unlocked.usage && typeof unlocked.usage.remaining === 'number' && unlocked.usage.cap > 0 && (
                <span className="ml-1 opacity-80">
                  · {unlocked.usage.remaining}/{unlocked.usage.cap} unlocks left this month
                </span>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // CTA state — show teaser + unlock button
  return (
    <div
      data-testid="rentcast-unlock-card"
      className="my-3 p-3 rounded-lg border border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-transparent"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Lock className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-500">
              Unlock RentCast-verified rent estimate
            </span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            See rent range, comparable count, and confidence level before analyzing the deal.
          </p>
          {error && <p className="text-[10px] text-red-400 mt-1.5">{error}</p>}
        </div>
        <Button
          size="sm"
          onClick={handleUnlock}
          disabled={loading}
          data-testid="rentcast-unlock-btn"
          className="bg-amber-500 hover:bg-amber-600 text-black font-semibold text-xs h-8 px-3 whitespace-nowrap"
        >
          {loading ? 'Unlocking…' : (isPro ? 'Unlock Verified Rent' : 'Upgrade to unlock')}
        </Button>
      </div>
    </div>
  );
};
